import type { NwsAlertGeometry, NwsAlertMapFeature } from '../services/nwsAlertsGeoService';
import { latLonPixelInTileGrid, tileGridBounds, type TileGridBounds } from './mapTiles';

export type LonLat = [lon: number, lat: number];

export type LonLatBounds = {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
};

/** NWS-style alert box color class from event name. */
export function nwsAlertOverlayClass(event: string): string {
  const e = event.toLowerCase();
  if (e.includes('tornado warning')) return 'weather-radar-alert--tornado-warning';
  if (e.includes('severe thunderstorm warning')) return 'weather-radar-alert--svr-warning';
  if (e.includes('flash flood warning') || e.includes('flood warning'))
    return 'weather-radar-alert--flood-warning';
  if (e.includes('tornado watch')) return 'weather-radar-alert--tornado-watch';
  if (e.includes('severe thunderstorm watch')) return 'weather-radar-alert--svr-watch';
  if (e.includes('flash flood watch') || e.includes('flood watch'))
    return 'weather-radar-alert--flood-watch';
  if (e.includes('special weather statement')) return 'weather-radar-alert--statement';
  if (e.includes('warning')) return 'weather-radar-alert--warning';
  if (e.includes('watch')) return 'weather-radar-alert--watch';
  if (e.includes('advisory')) return 'weather-radar-alert--advisory';
  return 'weather-radar-alert--other';
}

export function geometryRings(geometry: NwsAlertGeometry): LonLat[][] {
  if (geometry.type === 'Polygon') return geometry.coordinates as LonLat[][];
  return (geometry.coordinates as LonLat[][][]).flat();
}

/** Exterior rings only — GeoJSON hole rings must not be filled as separate alerts. */
export function exteriorGeometryRings(geometry: NwsAlertGeometry): LonLat[][] {
  if (geometry.type === 'Polygon') {
    const rings = geometry.coordinates as LonLat[][];
    return rings[0] ? [rings[0]] : [];
  }
  const polygons = geometry.coordinates as LonLat[][][];
  return polygons
    .map(poly => poly[0])
    .filter((ring): ring is LonLat[] => !!ring && ring.length >= 3);
}

/** Drop GeoJSON closing duplicate (last vertex equals first). */
export function normalizeRingVertices(ring: LonLat[]): LonLat[] {
  if (ring.length < 2) return ring;
  const [lon0, lat0] = ring[0]!;
  const [lonN, latN] = ring[ring.length - 1]!;
  if (lon0 === lonN && lat0 === latN) return ring.slice(0, -1);
  return ring;
}

export function ringBounds(ring: LonLat[]): LonLatBounds {
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const [lon, lat] of ring) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return { minLon, maxLon, minLat, maxLat };
}

export function boundsIntersect(a: LonLatBounds, b: TileGridBounds): boolean {
  return !(
    a.maxLat < b.minLat ||
    a.minLat > b.maxLat ||
    a.maxLon < b.minLon ||
    a.minLon > b.maxLon
  );
}

export function alertIntersectsMapView(
  alert: NwsAlertMapFeature,
  centerLat: number,
  centerLon: number,
  zoom: number,
  radius: number
): boolean {
  const mapBounds = tileGridBounds(centerLat, centerLon, zoom, radius);
  for (const ring of exteriorGeometryRings(alert.geometry)) {
    if (ring.length < 3) continue;
    if (boundsIntersect(ringBounds(ring), mapBounds)) return true;
  }
  return false;
}

export function filterAlertsForMapView(
  alerts: NwsAlertMapFeature[],
  centerLat: number,
  centerLon: number,
  zoom: number,
  radius: number
): NwsAlertMapFeature[] {
  return alerts.filter(a => alertIntersectsMapView(a, centerLat, centerLon, zoom, radius));
}

/** SVG path d for one ring in tile-grid pixel space. */
export function ringToSvgPath(
  ring: LonLat[],
  centerLat: number,
  centerLon: number,
  zoom: number,
  radius: number
): string {
  const parts: string[] = [];
  for (let i = 0; i < ring.length; i++) {
    const [lon, lat] = ring[i]!;
    const { left, top } = latLonPixelInTileGrid(lat, lon, zoom, radius, centerLat, centerLon);
    parts.push(`${i === 0 ? 'M' : 'L'}${left.toFixed(1)},${top.toFixed(1)}`);
  }
  return `${parts.join(' ')} Z`;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** SVG overlay of warning/watch polygons sized to the radar tile grid. */
export function buildNwsAlertsSvg(
  alerts: NwsAlertMapFeature[],
  centerLat: number,
  centerLon: number,
  zoom: number,
  radius: number,
  gridPx: number
): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'weather-radar-alerts-svg');
  svg.setAttribute('viewBox', `0 0 ${gridPx} ${gridPx}`);
  svg.setAttribute('width', String(gridPx));
  svg.setAttribute('height', String(gridPx));
  svg.setAttribute('aria-hidden', 'true');

  const visible = filterAlertsForMapView(alerts, centerLat, centerLon, zoom, radius);
  for (const alert of visible) {
    const styleClass = nwsAlertOverlayClass(alert.event);
    for (const ring of exteriorGeometryRings(alert.geometry)) {
      if (ring.length < 3) continue;
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('class', `weather-radar-alert ${styleClass}`);
      path.setAttribute('d', ringToSvgPath(ring, centerLat, centerLon, zoom, radius));
      const title = document.createElementNS(SVG_NS, 'title');
      title.textContent = `${alert.event} — ${alert.headline}`;
      path.appendChild(title);
      svg.appendChild(path);
    }
  }

  return svg;
}
